import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Endpoints that shouldn't trigger the refresh flow (they're either the
// refresh call itself or auth attempts where we just surface the error)
function isAuthEndpoint(url?: string): boolean {
  return !!url && (
    url.includes('/auth/refresh') ||
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/oauth')
  );
}

// Single-flight refresh so concurrent 401s only refresh once
let refreshPromise: Promise<string | null> | null = null;

function refreshSession(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/auth/refresh`, null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      .then((response) => {
        const newToken = response.data.token as string;
        localStorage.setItem('token', newToken);
        return newToken;
      })
      .catch(() => {
        localStorage.removeItem('token');
        // Let the app know the session is really gone (so the UI stops
        // showing the user as logged in)
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// On 401, try to silently refresh the token once and retry the original
// request. If that fails, clear the session so the UI reflects reality.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retriedAuth &&
      !isAuthEndpoint(originalRequest.url) &&
      localStorage.getItem('token')
    ) {
      originalRequest._retriedAuth = true;
      const newToken = await refreshSession();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  canRegister: () => api.get('/auth/can-register'),
  register: (data: { username: string; password: string; email?: string }) =>
    api.post('/auth/register', data),
  login: (data: { username: string; password: string }) =>
    api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  resetPassword: (data: { username: string; password: string }) =>
    api.post('/auth/reset-password', data)
};

// Documents API
export interface Document {
  id: number;
  title: string;
  slug: string;
  content?: string;
  folder_path: string;
  is_private?: boolean;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export const docsApi = {
  getAll: (params?: { folder?: string; tag?: string; search?: string }) =>
    api.get<Document[]>('/docs', { params }),
  getBySlug: (slug: string) => api.get<Document>(`/docs/${slug}`),
  create: (data: {
    title: string;
    content: string;
    folder_path?: string;
    is_private?: boolean;
    tags?: string[];
  }) => api.post<Document>('/docs', data),
  update: (
    id: number,
    data: {
      title?: string;
      content?: string;
      folder_path?: string;
      is_private?: boolean;
      tags?: string[];
    }
  ) => api.put<Document>(`/docs/${id}`, data),
  delete: (id: number) => api.delete(`/docs/${id}`),
  getFolders: () => api.get<string[]>('/docs/meta/folders'),
  getTags: () => api.get<string[]>('/docs/meta/tags')
};

// Users API
export interface User {
  id: number;
  username: string;
  email?: string;
  is_admin: boolean;
  created_at: string;
  oauth_provider?: string;
}

export const usersApi = {
  getAll: () => api.get<User[]>('/users'),
  create: (data: { username: string; password: string; email?: string }) =>
    api.post<User>('/users', data),
  delete: (id: number) => api.delete(`/users/${id}`)
};

// OAuth API
export interface OAuthConfig {
  enabled: boolean;
  providerName: string;
}

export const oauthApi = {
  getConfig: () => api.get<OAuthConfig>('/auth/oauth/config'),
  // The authorize endpoint is accessed via redirect, not API call
  getAuthorizeUrl: () => `${API_BASE_URL}/auth/oauth/authorize`,
  unlink: () => api.post('/auth/oauth/unlink')
};

// Images API
export interface ImageInfo {
  filename: string;
  url: string;
  size: number;
  uploadedAt: string;
}

export const imagesApi = {
  upload: async (file: File): Promise<ImageInfo> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await api.post<ImageInfo>('/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },
  list: () => api.get<ImageInfo[]>('/images'),
  delete: (filename: string) => api.delete(`/images/${filename}`)
};

// Ollama AI API
export interface OllamaConfig {
  enabled: boolean;
  model: string;
}

export interface OllamaChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export const ollamaApi = {
  getConfig: () => api.get<OllamaConfig>('/ollama/config'),
  generate: (data: { prompt: string; context?: string }) =>
    api.post<{ markdown: string }>('/ollama/generate', data),
  chat: (data: { messages: OllamaChatMessage[] }) =>
    api.post<{ response: string }>('/ollama/chat', data),
  createDocument: (data: {
    prompt: string;
    title?: string;
    folder_path?: string;
    is_private?: boolean;
    tags?: string[];
  }) => api.post<Document>('/ollama/create-document', data)
};

export default api;
